/** Document types that use the event layout (main column + sticky Event Key Details). Must match Umbraco aliases. */
const EVENT_PAGE_DOCUMENT_ALIASES = new Set(['trainingEvent', 'eventsConferences', 'hubPage'])

/** Supports both normalised list items ({ name, data, jumpToId }) and raw API items ({ componentName, componentData, jumpToId }). */
export function getComponentNameAndData(entry) {
  if (!entry) {
    return { typeKey: undefined, payload: undefined, jumpToId: undefined }
  }
  const rawName = entry.name ?? entry.componentName
  const payload = entry.data ?? entry.componentData
  const typeKey = typeof rawName === 'string' ? rawName.trim() : rawName
  const jumpToId = entry.jumpToId
  return { typeKey, payload, jumpToId }
}

/** Stable in-page anchor for Jump To and deep links. */
export function getComponentAnchorId(blockKey, fallbackIndex) {
  if (typeof blockKey === 'string' && blockKey.length > 0) {
    return `component-${blockKey}`
  }
  return `component-page-${fallbackIndex}`
}

function normalizeContentTypeAlias(res) {
  const raw = res?.contentTypeAlias ?? res?.ContentTypeAlias
  if (typeof raw !== 'string') {
    return null
  }
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

function isEventDocumentPage(res) {
  const alias = normalizeContentTypeAlias(res)
  if (alias === null) {
    return false
  }
  if (EVENT_PAGE_DOCUMENT_ALIASES.has(alias)) {
    return true
  }
  const lower = alias.toLowerCase()
  for (const known of EVENT_PAGE_DOCUMENT_ALIASES) {
    if (known.toLowerCase() === lower) {
      return true
    }
  }
  return false
}

/**
 * Use the event key sidebar grid when the page is an event document type, or when the API omits
 * contentTypeAlias but the component order matches the event pattern (prepended section header + key details).
 */
export function shouldUseEventKeySidebarLayout(res, hasSidebar, leadingCount) {
  if (!hasSidebar) {
    return false
  }
  if (isEventDocumentPage(res)) {
    return true
  }
  if (normalizeContentTypeAlias(res) !== null) {
    return false
  }
  return leadingCount > 0
}

/**
 * Consecutive sectionHeader blocks stay full-width; eventKeyDetails goes in the sidebar;
 * remaining blocks fill the main column.
 */
export function partitionForEventKeySidebar(componentsList) {
  const leading = []
  let i = 0
  while (i < componentsList.length) {
    const { typeKey } = getComponentNameAndData(componentsList[i])
    if (typeKey !== 'sectionHeader') {
      break
    }
    leading.push({ comp: componentsList[i], idx: i })
    i += 1
  }

  const tail = componentsList.slice(i).map((comp, j) => ({ comp, idx: i + j }))
  const sidebar = tail.filter(({ comp }) => getComponentNameAndData(comp).typeKey === 'eventKeyDetails')
  const main = tail.filter(({ comp }) => getComponentNameAndData(comp).typeKey !== 'eventKeyDetails')
  return { leading, main, sidebar, hasSidebar: sidebar.length > 0 }
}

/** Last teaser block that receives the locked-page fade (excludes sidebar key details). */
export function getLockedPreviewBoundaryComp(previewList, useEventSidebarLayout) {
  if (!previewList.length) {
    return null
  }
  if (!useEventSidebarLayout) {
    return previewList[previewList.length - 1]
  }
  const { leading, main } = partitionForEventKeySidebar(previewList)
  if (main.length > 0) {
    return main[main.length - 1].comp
  }
  if (leading.length > 0) {
    return leading[leading.length - 1].comp
  }
  return null
}
